import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { UserPlus, Plus, DollarSign, Edit, Camera, Trash2 } from 'lucide-react';
import Webcam from 'react-webcam';
import { AdminResetUserPassword } from '@/components/PasswordReset';

export default function EmployeeManagement({ token }) {
  const [employees, setEmployees] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const webcamRef = useRef(null);
  
  const [createForm, setCreateForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    position: 'Field Worker',
    base_salary: '25000',
    allowances: '0',
    deductions: '0',
    office_address: '',
    office_latitude: '',
    office_longitude: '',
    attendance_radius_km: '50',
    assigned_turf_ids: []
  });
  
  const [salaryForm, setSalaryForm] = useState({
    position: '',
    base_salary: '',
    allowances: '',
    deductions: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchGrounds();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/admin/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };
  
  const fetchGrounds = async () => {
    try {
      const response = await axios.get(`${API}/grounds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrounds(response.data);
    } catch (error) {
      console.error('Failed to fetch grounds:', error);
    }
  };

  const captureImage = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setShowCamera(false);
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    if (!capturedImage) {
      toast.error('Please capture employee photo', { 
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#dc2626', color: '#fff' }
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        ...createForm,
        role: 'employee',
        face_image: capturedImage,
        base_salary: parseFloat(createForm.base_salary),
        allowances: parseFloat(createForm.allowances),
        deductions: parseFloat(createForm.deductions),
        office_latitude: createForm.office_latitude ? parseFloat(createForm.office_latitude) : null,
        office_longitude: createForm.office_longitude ? parseFloat(createForm.office_longitude) : null,
        attendance_radius_km: parseFloat(createForm.attendance_radius_km),
        assigned_turf_ids: createForm.assigned_turf_ids
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Employee created successfully!', { 
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#00FF66', color: '#000', fontWeight: 'bold' }
      });
      setCreateDialogOpen(false);
      setCreateForm({
        full_name: '',
        email: '',
        phone: '',
        password: '',
        position: 'Field Worker',
        base_salary: '25000',
        allowances: '0',
        deductions: '0',
        office_address: '',
        office_latitude: '',
        office_longitude: '',
        attendance_radius_km: '50',
        assigned_turf_ids: []
      });
      setCapturedImage(null);
      fetchEmployees();
    } catch (error) {
      console.error('Create employee error:', error);
      toast.error(`❌ Failed: ${error.response?.data?.detail || 'Could not create employee'}`, {
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#dc2626', color: '#fff' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openSalaryDialog = (employee) => {
    setSelectedEmployee(employee);
    setSalaryForm({
      position: employee.position || '',
      base_salary: employee.base_salary?.toString() || '',
      allowances: employee.allowances?.toString() || '',
      deductions: employee.deductions?.toString() || ''
    });
    setSalaryDialogOpen(true);
  };

  const handleUpdateSalary = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      await axios.put(
        `${API}/admin/employee/${selectedEmployee.id}/salary`,
        {
          position: salaryForm.position || null,
          base_salary: salaryForm.base_salary ? parseFloat(salaryForm.base_salary) : null,
          allowances: salaryForm.allowances ? parseFloat(salaryForm.allowances) : null,
          deductions: salaryForm.deductions ? parseFloat(salaryForm.deductions) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('✅ Salary updated successfully!', {
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#00FF66', color: '#000', fontWeight: 'bold' }
      });
      setSalaryDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      console.error('Update salary error:', error);
      toast.error(`❌ Failed: ${error.response?.data?.detail || 'Could not update salary'}`, {
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#dc2626', color: '#fff' }
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete ${employeeName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/employee/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`✅ Employee deleted: ${employeeName}`, {
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#00FF66', color: '#000', fontWeight: 'bold' }
      });
      fetchEmployees();
    } catch (error) {
      console.error('Delete employee error:', error);
      toast.error(`❌ Failed: ${error.response?.data?.detail || 'Could not delete employee'}`, {
        position: 'top-center',
        duration: 5000,
        style: { backgroundColor: '#dc2626', color: '#fff' }
      });
    }
  };

  return (
    <Card className="p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
          <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
          Employee Management
        </h3>
        
        {/* Create Employee Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-employee-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Employee Account</DialogTitle>
              <DialogDescription>Add employee with photo, salary, and login details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateEmployee} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  placeholder="John Doe"
                  value={createForm.full_name}
                  onChange={(e) => setCreateForm({...createForm, full_name: e.target.value})}
                  required
                  data-testid="create-emp-name"
                />
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                  required
                  data-testid="create-emp-email"
                />
              </div>
              
              <div>
                <Label>Phone *</Label>
                <Input
                  placeholder="+919876543210"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                  required
                  data-testid="create-emp-phone"
                />
              </div>
              
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  placeholder="Create password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  required
                  data-testid="create-emp-password"
                />
              </div>
              
              <Separator />
              
              <div>
                <Label>Position/Role</Label>
                <Input
                  placeholder="Field Worker, Supervisor, etc."
                  value={createForm.position}
                  onChange={(e) => setCreateForm({...createForm, position: e.target.value})}
                  data-testid="create-emp-position"
                />
              </div>
              
              <div>
                <Label>Base Salary (₹/month)</Label>
                <Input
                  type="number"
                  placeholder="25000"
                  value={createForm.base_salary}
                  onChange={(e) => setCreateForm({...createForm, base_salary: e.target.value})}
                  required
                  data-testid="create-emp-salary"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Allowances (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={createForm.allowances}
                    onChange={(e) => setCreateForm({...createForm, allowances: e.target.value})}
                    data-testid="create-emp-allowances"
                  />
                </div>
                <div>
                  <Label>Deductions (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={createForm.deductions}
                    onChange={(e) => setCreateForm({...createForm, deductions: e.target.value})}
                    data-testid="create-emp-deductions"
                  />
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label>Assigned Office Address *</Label>
                <Input
                  placeholder="Mumbai Office, Maharashtra"
                  value={createForm.office_address}
                  onChange={(e) => setCreateForm({...createForm, office_address: e.target.value})}
                  required
                  data-testid="create-emp-office-address"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Office Latitude *</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="19.0760"
                    value={createForm.office_latitude}
                    onChange={(e) => setCreateForm({...createForm, office_latitude: e.target.value})}
                    required
                    data-testid="create-emp-latitude"
                  />
                </div>
                <div>
                  <Label>Office Longitude *</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="72.8777"
                    value={createForm.office_longitude}
                    onChange={(e) => setCreateForm({...createForm, office_longitude: e.target.value})}
                    required
                    data-testid="create-emp-longitude"
                  />
                </div>
              </div>
              
              <div>
                <Label>Attendance Radius (km)</Label>
                <Input
                  type="number"
                  placeholder="50"
                  value={createForm.attendance_radius_km}
                  onChange={(e) => setCreateForm({...createForm, attendance_radius_km: e.target.value})}
                  data-testid="create-emp-radius"
                />
              </div>
              
              <Separator />
              
              <div>
                <Label>Assign Turfs/Locations (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">Select turfs where this employee can mark attendance</p>
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3" style={{backgroundColor: '#000', borderColor: '#333'}}>
                  {grounds.length === 0 ? (
                    <p className="text-sm text-gray-500">No turfs available. Create turfs first.</p>
                  ) : (
                    grounds.map((ground) => (
                      <label key={ground.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={createForm.assigned_turf_ids.includes(ground.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCreateForm({
                                ...createForm,
                                assigned_turf_ids: [...createForm.assigned_turf_ids, ground.id]
                              });
                            } else {
                              setCreateForm({
                                ...createForm,
                                assigned_turf_ids: createForm.assigned_turf_ids.filter(id => id !== ground.id)
                              });
                            }
                          }}
                          className="rounded"
                          style={{accentColor: '#00FF66'}}
                        />
                        <span className="text-sm" style={{color: '#FFF'}}>{ground.name} - {ground.address}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs mt-2" style={{color: '#888'}}>
                  Selected: {createForm.assigned_turf_ids.length} turf(s)
                </p>
              </div>
              
              <Separator />
              
              <div>
                <Label>Employee Photo * (Live Capture)</Label>
                {!showCamera && !capturedImage && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() => setShowCamera(true)}
                    data-testid="create-emp-camera-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Capture Photo
                  </Button>
                )}

                {showCamera && (
                  <div className="mt-2 space-y-2">
                    <Webcam
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      className="w-full rounded-lg"
                    />
                    <div className="flex gap-2">
                      <Button type="button" onClick={captureImage} className="flex-1" data-testid="capture-emp-photo-btn">
                        Capture
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowCamera(false)} className="flex-1">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {capturedImage && (
                  <div className="mt-2 relative">
                    <img src={capturedImage} alt="Employee" className="w-full rounded-lg" />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setCapturedImage(null)}
                    >
                      Retake
                    </Button>
                  </div>
                )}
              </div>
              
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-create-emp-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Employee Account
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Employee List */}
      <ScrollArea className="h-96">
        <div className="space-y-3">
          {employees.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No employees yet</p>
          ) : (
            employees.map((emp) => (
              <div key={emp.id} className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200" data-testid={`employee-card-${emp.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {emp.face_image ? (
                      <img src={emp.face_image} alt={emp.full_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {emp.full_name.split(' ').map(n => n[0]).join('')}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-800">{emp.full_name}</p>
                      <p className="text-sm text-gray-600">{emp.email}</p>
                      <Badge variant="outline" className="mt-1">{emp.position || 'Employee'}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AdminResetUserPassword 
                      userId={emp.id}
                      userName={emp.full_name}
                      token={token}
                      onSuccess={fetchEmployees}
                    />
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openSalaryDialog(emp)}
                      data-testid={`edit-salary-btn-${emp.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDeleteEmployee(emp.id, emp.full_name)}
                      data-testid={`delete-employee-btn-${emp.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <Separator className="my-2" />
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-gray-600">Base Salary</p>
                    <p className="font-semibold text-blue-600">₹{emp.base_salary?.toLocaleString() || '30,000'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Allowances</p>
                    <p className="font-semibold text-green-600">+₹{emp.allowances?.toLocaleString() || '0'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Deductions</p>
                    <p className="font-semibold text-red-600">-₹{emp.deductions?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Salary Edit Dialog */}
      <Dialog open={salaryDialogOpen} onOpenChange={setSalaryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Salary Configuration</DialogTitle>
            <DialogDescription>Change position, base salary, allowances, and deductions</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <form onSubmit={handleUpdateSalary} className="space-y-4">
              <div>
                <Label>Employee</Label>
                <Input value={selectedEmployee.full_name} disabled />
              </div>
              
              <div>
                <Label>Position/Role</Label>
                <Input
                  placeholder="Field Worker, Supervisor, etc."
                  value={salaryForm.position}
                  onChange={(e) => setSalaryForm({...salaryForm, position: e.target.value})}
                  data-testid="edit-position"
                />
              </div>
              
              <div>
                <Label>Base Salary (₹/month)</Label>
                <Input
                  type="number"
                  placeholder="25000"
                  value={salaryForm.base_salary}
                  onChange={(e) => setSalaryForm({...salaryForm, base_salary: e.target.value})}
                  data-testid="edit-salary"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Allowances (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={salaryForm.allowances}
                    onChange={(e) => setSalaryForm({...salaryForm, allowances: e.target.value})}
                    data-testid="edit-allowances"
                  />
                </div>
                <div>
                  <Label>Deductions (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={salaryForm.deductions}
                    onChange={(e) => setSalaryForm({...salaryForm, deductions: e.target.value})}
                    data-testid="edit-deductions"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> These changes will affect the employee's next payroll calculation
                </p>
              </div>
              
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="update-salary-btn">
                <DollarSign className="h-4 w-4 mr-2" />
                Update Salary
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
